import { type FC, useState, useEffect } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { X } from "lucide-react";
import { CallSupportLine } from "@/shared/components/CallSupportLine";
import { EstateExpensePill } from "@/shared/components/EstateExpensePill";
import { EstateExpenseModal } from "@/shared/components/EstateExpenseModal";
import { TierCardGrid } from "@/shared/components/TierCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useCheckout } from "@/shared/hooks/useCheckout";
import { usePricingViewTracking } from "@/shared/hooks/usePricingViewTracking";
import { trackEvent, EventType } from "@/shared/utils/core";
import { cn } from "@/shared/utils/cn";
import type { PromptLocation } from "@/shared/utils/funnelAnalytics";
import type { PaymentTier } from "@/shared/utils/tierOrder";
import { IconButton } from "@/shared/components/ui/icon-button";

interface PostLossPaywallProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  promptLocation: PromptLocation;
  currentTier?: PaymentTier | null;
  returnPath?: string;
}

const TITLES = {
  free: "Pick the support that\u2019s right for you",
  basic: "Ready to uncover the full estate?",
  premium: "Ready to settle the estate?",
} as const;

const PostLossPaywall: FC<PostLossPaywallProps> = ({
  isOpen,
  onOpenChange,
  promptLocation,
  currentTier = "free",
  returnPath,
}) => {
  const { person } = useAppContext();
  const [showLearnMore, setShowLearnMore] = useState(false);
  const { trackDismissed } = usePricingViewTracking({
    isOpen,
    promptLocation,
    currentTier,
    estateId: person?.estate_id,
  });

  useEffect(() => {
    if (isOpen) {
      trackEvent(EventType.UPGRADE_PROMPT_VIEWED, {
        prompt_location: promptLocation,
        current_tier: currentTier,
      });
    }
  }, [isOpen, currentTier, promptLocation]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      trackDismissed();
    }
    onOpenChange(open);
  };

  const resolvedPath = returnPath || window.location.pathname;
  const { startCheckout, loadingTier, isLoading } = useCheckout({
    returnUrl: `${window.location.origin}${resolvedPath}`,
    cancelUrl: `${window.location.origin}${resolvedPath}`,
    context: "POST_LOSS_PAYWALL",
    promptLocation,
    fromTier: currentTier,
  });

  const tierKey = currentTier === "basic" || currentTier === "premium" ? currentTier : "free";
  const title = TITLES[tierKey];

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            "h-auto max-h-[100dvh] w-full gap-0 overflow-y-auto p-6 sm:max-h-[90dvh] sm:rounded-xl",
            currentTier === "premium" ? "max-w-[750px]" : "max-w-[1000px]",
          )}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogClose asChild>
            <IconButton className="absolute right-4 top-4 z-10" label="Close" tooltip={false}>
              <X />
            </IconButton>
          </DialogClose>
          <DialogBody className="flex flex-col items-center gap-6 border-t-0">
            <div className="flex flex-col items-center gap-3">
              <h2 className="text-center">{title}</h2>
              <EstateExpensePill onLearnMore={() => setShowLearnMore(true)} />
            </div>

            <TierCardGrid
              currentTier={currentTier}
              onCheckout={startCheckout}
              isLoading={isLoading}
              loadingTier={loadingTier}
            />

            <CallSupportLine />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <EstateExpenseModal open={showLearnMore} onOpenChange={setShowLearnMore} />
    </>
  );
};

export default PostLossPaywall;
